import { useState, useEffect, useCallback, useRef } from "react";
import { scrollTo } from "@/hooks/useSmoothScroll";
import logoFarbig from "@/assets/logo-farbig.png";
import logoWeiss from "@/assets/logo-weiss.webp";
import navFuerWenImg from "@/assets/navbar-fuer-wen-einfamilienhaus.png";
import navGewerbeImg from "@/assets/navbar-gewerbe.webp";
import headerLoesungenLandwirtschaftPv from "@/assets/header-loesungen-landwirtschaft-pv.png";
import LiveStatusBadge, { useAvailabilityStatus } from "@/components/feature/LiveStatusBadge";

interface DropdownItem {
  label: string;
  href: string;
  description?: string;
}

interface DropdownMenu {
  id: string;
  label: string;
  items: DropdownItem[];
  secondaryItems?: DropdownItem[];
  columnTitle?: string;
  secondaryColumnTitle?: string;
  image: string;
  imageAlt: string;
}

const dropdownMenus: DropdownMenu[] = [
  {
    id: "loesungen",
    label: "Lösungen",
    items: [
      { label: "Photovoltaik & Speicher", description: "Maximale Eigenstrom-Nutzung", href: "/photovoltaik" },
      { label: "Stromspeicher", description: "Solarstrom rund um die Uhr", href: "/stromspeicher" },
      { label: "Wallbox & E-Mobilität", description: "Laden zuhause & unterwegs", href: "/wallbox" },
    ],
    secondaryItems: [
      { label: "Smart Home", description: "Intelligente Vernetzung", href: "/smart-home" },
      { label: "Ersatzstrom", description: "Sicherheit bei Netzausfall", href: "/notstrom" },
      { label: "E-Check & Service", description: "Rundum-sorglos-Wartung", href: "/e-check" },
      { label: "G2 Contracting & Leistungen", description: "Mieterstrom, Energiemanagement, dynamische Tarife", href: "/g2-contracting" },
    ],
    image: headerLoesungenLandwirtschaftPv,
    imageAlt: "Großes landwirtschaftliches Gebäude mit Photovoltaik-Anlage auf dem Dach",
  },
  {
    id: "fuer-wen",
    label: "Für wen",
    items: [
      { label: "Einfamilienhaus", description: "Maßgeschneiderte Solar-Lösungen", href: "/einfamilienhaus" },
      { label: "Mehrfamilienhaus", description: "Gemeinsam günstiger Strom", href: "/mehrfamilienhaus" },
      { label: "Gewerbe", description: "Effiziente Energie für Unternehmen", href: "/gewerbe" },
      { label: "Landwirtschaft", description: "Rendite von großen Dachflächen", href: "/landwirtschaft" },
    ],
    image: navFuerWenImg,
    imageAlt: "Backstein-Einfamilienhaus mit schwarzer Photovoltaik-Anlage auf dem Satteldach in Emmerich am Rhein",
  },
  {
    id: "unternehmen",
    label: "Unternehmen",
    columnTitle: "ZWEERING ELEKTROTECHNIK",
    items: [
      { label: "Über uns", description: "Der Betrieb hinter dem Handwerk", href: "/ueber-uns" },
      { label: "Karriere", description: "Offene Stellen & Perspektive", href: "/karriere" },
    ],
    secondaryColumnTitle: "G2",
    secondaryItems: [
      { label: "G2 Contracting", description: "Die intelligente Energie-Schicht", href: "/g2-contracting" },
      { label: "G2 Leistungen", description: "Mieterstrom, Contracting, Energiemanagement", href: "/g2-contracting/leistungen" },
    ],

    image: navGewerbeImg,
    imageAlt: "Modernes Gewerbegebäude aus rotem Backstein in der Abenddämmerung mit beleuchteten Büroräumen und Firmenparkplatz",
  },
];

function getSolutionIcon(label: string): string {
  const iconMap: Record<string, string> = {
    "Photovoltaik & Speicher": "ri-sun-line",
    "Wärmepumpe": "ri-temp-hot-line",
    "Wallbox & E-Mobilität": "ri-car-line",
    "Smart Home": "ri-home-smile-line",
    "Ersatzstrom": "ri-flashlight-line",
    "E-Check & Service": "ri-shield-check-line",
    "G2 Contracting & Leistungen": "ri-flashlight-line",
    "Einfamilienhaus": "ri-home-4-line",
    "Gewerbe": "ri-building-line",
    "Mehrfamilienhaus": "ri-building-2-line",
    "Landwirtschaft": "ri-plant-line",
    "Mieterstrom": "ri-building-line",
    "Energiemanagement": "ri-dashboard-line",
    "Für Vermieter & WEGs": "ri-hand-coin-line",
  };
  return iconMap[label] || "ri-arrow-right-line";
}

export default function Header() {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navHidden, setNavHidden] = useState(false);
  const [navFloating, setNavFloating] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);
  const availability = useAvailabilityStatus();
  const phoneIconColor =
    availability?.state === "open"
      ? "#4CAF50"
      : availability?.state === "lunch"
        ? "#FF9800"
        : availability?.state === "closed"
          ? "#D32F2F"
          : undefined; // Fallback: behält Default-Akzentfarbe
  const phoneIconPulse = availability?.state === "open";

  const handleScroll = useCallback(() => {
    const y = window.scrollY;
    const goingDown = y > lastScrollY.current + 5;
    const goingUp = y < lastScrollY.current - 5;
    lastScrollY.current = y;

    if (y <= 5) {
      setNavFloating(false);
      setNavHidden(false);
    } else if (goingDown && y > 100) {
      setNavHidden(true);
      setNavFloating(false);
    } else if (goingUp) {
      setNavHidden(false);
      setNavFloating(y > 60);
    }
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Don't close when mobile menu is open — the dropdown buttons live
      // outside the header ref (in the fixed overlay) and would otherwise
      // close immediately on click.
      if (mobileOpen) return;
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [mobileOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  const toggleDropdown = (id: string) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  const handleAnchorClick = (e: React.MouseEvent, target: string) => {
    e.preventDefault();
    scrollTo(target);
  };

  const isAnyDropdownOpen = activeDropdown !== null;

  const activeMenu = dropdownMenus.find((m) => m.id === activeDropdown);

  // Nav item style — slide-up bg from bottom on hover
  const navItemBase = "relative overflow-hidden px-3 h-11 rounded-full text-[15px] font-medium whitespace-nowrap transition-colors duration-300 cursor-pointer flex items-center";

  const navItemHover = (isDark: boolean) => {
    const textColor = isDark ? "text-foreground-700" : "text-white/90";
    const hoverText = isDark ? "hover:text-foreground-950" : "hover:text-white";
    return `${textColor} ${hoverText} before:absolute before:inset-0 before:rounded-full before:bg-white/15 before:scale-y-0 before:origin-bottom before:transition-transform before:duration-300 hover:before:scale-y-100`;
  };

  return (
    <>
      {/* Skip-Link: springt zum Hauptinhalt (nur bei Tastatur-Fokus sichtbar) */}
      <a href="#main-content" className="skip-link">
        Zum Inhalt springen
      </a>

      {/* Dark overlay when any dropdown is open */}
      {isAnyDropdownOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
          onClick={() => setActiveDropdown(null)}
          aria-hidden="true"
        />
      )}


      <header
        ref={headerRef}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-[350ms] ease-in-out ${
          navHidden && !isAnyDropdownOpen && !mobileOpen
            ? "-translate-y-full"
            : "translate-y-0"
        } ${
          isAnyDropdownOpen
            ? "mx-0 mt-0 rounded-none shadow-lg bg-white"
            : navFloating || mobileOpen
              ? "mx-4 md:mx-6 mt-3 rounded-2xl shadow-lg backdrop-blur-md bg-white/95"
              : "bg-transparent"
        }`}
      >
        <div className="relative flex items-center justify-between gap-4 xl:gap-8 px-6 md:px-10 lg:px-12 xl:px-14 h-20 md:h-24">
          {/* Logo */}
          <a href="/" className="flex items-center shrink-0 bg-transparent leading-none">
            <img
              src={navFloating || isAnyDropdownOpen || mobileOpen ? logoFarbig : logoWeiss}
              alt="Zweering Elektrotechnik"
              loading="eager"
              fetchPriority="high"
              className={`h-11 w-auto block transition-all duration-500 ${
                navFloating || isAnyDropdownOpen || mobileOpen
                  ? ""
                  : "[filter:drop-shadow(0_2px_8px_rgba(0,0,0,0.5))]"
              }`}
            />
          </a>

          {/* Desktop Nav — inline group */}
          <nav aria-label="Hauptnavigation" className="hidden lg:flex items-center gap-1 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 pointer-events-auto">
            {dropdownMenus.map((menu) => (
              <div
                key={menu.id}
                className="relative"
              >
                <button
                  onClick={() => toggleDropdown(menu.id)}
                  aria-haspopup="true"
                  aria-expanded={activeDropdown === menu.id}
                  aria-controls={`dropdown-panel-${menu.id}`}
                  className={`${navItemBase} ${navItemHover(navFloating || isAnyDropdownOpen || mobileOpen)} flex items-center gap-1`}
                >
                  <span className="relative z-10">{menu.label}</span>
                  <i
                    aria-hidden="true"
                    className={`ri-arrow-down-s-line w-3 h-3 flex items-center justify-center transition-transform duration-300 relative z-10 ${
                      activeDropdown === menu.id ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
            ))}

            <a
              href="/referenzen"
              className={`${navItemBase} ${navItemHover(navFloating || isAnyDropdownOpen || mobileOpen)}`}
            >
              <span className="relative z-10">Referenzen</span>
            </a>
          </nav>


          {/* Right side group */}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {/* Desktop: phone (icon-only on lg, with number on xl+) + Live-Status */}
            <div className="hidden lg:block relative">
              <a
                href="tel:+492822791010"
                aria-label="Anrufen: 02822 791010"
                className={`inline-flex items-center gap-2 rounded-full font-medium whitespace-nowrap transition-colors duration-300
                  2xl:px-4 2xl:h-11 2xl:text-[15px]
                  lg:w-11 lg:h-11 lg:justify-center lg:border 2xl:border-0 2xl:w-auto
                  ${
                    navFloating || isAnyDropdownOpen || mobileOpen
                      ? "text-foreground-800 hover:text-accent-500 lg:border-foreground-200 lg:hover:border-accent-500"
                      : "text-white hover:text-accent-500 lg:border-white/50 lg:hover:border-white"
                  }`}
              >
                <span className="relative inline-flex w-4 h-4 items-center justify-center">
                  {phoneIconPulse && (
                    <span
                      className="absolute inset-0 rounded-full opacity-50 motion-safe:animate-ping motion-reduce:hidden"
                      style={{ backgroundColor: phoneIconColor }}
                      aria-hidden="true"
                    />
                  )}
                  <i
                    className="ri-phone-line w-4 h-4 flex items-center justify-center text-base relative"
                    style={phoneIconColor ? { color: phoneIconColor } : undefined}
                  />
                </span>
                <span className="hidden 2xl:inline">02822 791010</span>
              </a>
              {/* Status-Badge mittig unter dem Telefon-Button — absolut positioniert, beeinflusst die Zeilenhöhe nicht */}
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 z-40 pointer-events-auto">
                <LiveStatusBadge
                  variant={navFloating || isAnyDropdownOpen || mobileOpen ? "dark" : "light"}
                />
              </div>
            </div>

            {/* Desktop: customer login placeholder */}
            <div className="relative hidden lg:block group">
              <button
                type="button"
                aria-label="Kundenportal — in Kürze verfügbar"
                disabled
                className={`flex items-center justify-center w-11 h-11 rounded-full border cursor-not-allowed transition-colors ${
                  navFloating || isAnyDropdownOpen || mobileOpen
                    ? "border-foreground-200 text-foreground-600"
                    : "border-white/50 text-white"
                }`}
              >
                <i className="ri-user-line w-4 h-4 flex items-center justify-center text-sm" />
              </button>
              <span
                className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 text-[15px] font-medium whitespace-nowrap opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 ${
                  navFloating || isAnyDropdownOpen || mobileOpen
                    ? "text-foreground-800"
                    : "text-white"
                }`}
              >
                in Kürze
              </span>
            </div>

            {/* CTA — visible on desktop and as compact pill on mobile */}
            <a
              href="/kontakt"
              className={`btn-slide hidden md:inline-flex items-center px-5 h-11 rounded-full border text-[15px] font-medium uppercase tracking-wider whitespace-nowrap ${
                navFloating || isAnyDropdownOpen || mobileOpen
                  ? "btn-fill-dark border-foreground-200 text-foreground-800"
                  : "btn-fill-white border-white/50 text-white"
              }`}
            >
              Angebot anfragen
            </a>

            {/* Hamburger — mobile only */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-menu"
              className={`flex items-center justify-center w-11 h-11 rounded-full border transition-colors ${
                navFloating || isAnyDropdownOpen || mobileOpen
                  ? "border-foreground-200 hover:border-foreground-400 text-foreground-700"
                  : "border-white/50 hover:border-white/80 text-white"
              } lg:hidden`}
            >
              <i aria-hidden="true" className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} w-4 h-4 flex items-center justify-center text-sm`} />
            </button>

          </div>
        </div>


        {/* Unified Mega Dropdown — all three menus use this identical panel */}
        {isAnyDropdownOpen && activeMenu && (
          <div
            id={`dropdown-panel-${activeMenu.id}`}
            role="region"
            aria-label={activeMenu.label}
            className={`hidden lg:block absolute top-full left-0 right-0 bg-white shadow-2xl overflow-hidden animate-dropdown-enter ${
              isAnyDropdownOpen ? "rounded-none w-full" : "rounded-2xl !w-screen !-ml-[calc(50vw-50%)]"
            }`}
          >
            <div className="w-full max-w-screen-2xl mx-auto px-8 md:px-14 lg:px-20">
              <div className="flex py-10 gap-0">
                {/* Image left */}
                <div className="w-[400px] shrink-0 relative overflow-hidden rounded-xl">
                  {activeMenu.image ? (
                    <>
                      <img
                        src={activeMenu.image}
                        alt={activeMenu.imageAlt}
                        loading="lazy"
                        className="w-full h-[320px] object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-white/60 pointer-events-none" />
                    </>
                  ) : (
                    <div className="w-full h-[320px] flex items-center justify-center bg-foreground-100">
                      <div className="text-center">
                        <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-foreground-200/40 flex items-center justify-center">
                          <i className="ri-image-add-line text-foreground-400 text-xl w-6 h-6 flex items-center justify-center"></i>
                        </div>
                        <span className="text-sm text-foreground-400 font-label tracking-wider uppercase">
                          Bild folgt
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Content right */}
                <div className="flex-1 pl-16 pr-0 min-w-0 flex flex-col justify-center">
                  {/* Eyebrow */}
                  <h3 className="font-heading text-accent-500 text-xs uppercase tracking-[0.2em] mb-8">
                    {activeMenu.label}
                  </h3>

                  {/* Entries */}
                  {activeMenu.secondaryItems ? (
                    <div className="flex gap-20">
                      <div className="flex-1">
                        {activeMenu.columnTitle && (
                          <h4 className="font-heading text-accent-500 text-[11px] uppercase tracking-[0.18em] mb-4">
                            {activeMenu.columnTitle}
                          </h4>
                        )}
                        <ul className="space-y-5">
                          {activeMenu.items.map((item) => (
                            <li key={item.label}>
                              <a
                                href={item.href}
                                className="group flex items-start gap-3 rounded-lg -m-1 p-1 hover:bg-accent-50 transition-colors"
                                onClick={() => setActiveDropdown(null)}
                              >
                                <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center shrink-0 group-hover:bg-accent-100 transition-colors duration-200">
                                  <i className={`${getSolutionIcon(item.label)} w-4 h-4 flex items-center justify-center text-accent-500`} />
                                </span>
                                <div>
                                  <span className="block font-heading font-semibold text-foreground-900 group-hover:text-accent-500 transition-colors duration-200 text-sm leading-tight">
                                    {item.label}
                                  </span>
                                  {item.description && (
                                    <span className="block text-foreground-500 text-xs mt-0.5 leading-relaxed">
                                      {item.description}
                                    </span>
                                  )}
                                </div>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="flex-1">
                        {activeMenu.secondaryColumnTitle && (
                          <h4 className="font-heading text-accent-500 text-[11px] uppercase tracking-[0.18em] mb-4">
                            {activeMenu.secondaryColumnTitle}
                          </h4>
                        )}
                        <ul className="space-y-5">
                          {activeMenu.secondaryItems.map((item) => (
                            <li key={item.label}>
                              <a
                                href={item.href}
                                className="group flex items-start gap-3 rounded-lg -m-1 p-1 hover:bg-accent-50 transition-colors"
                                onClick={() => setActiveDropdown(null)}
                              >
                                <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center shrink-0 group-hover:bg-accent-100 transition-colors duration-200">
                                  <i className={`${getSolutionIcon(item.label)} w-4 h-4 flex items-center justify-center text-accent-500`} />
                                </span>
                                <div>
                                  <span className="block font-heading font-semibold text-foreground-900 group-hover:text-accent-500 transition-colors duration-200 text-sm leading-tight">
                                    {item.label}
                                  </span>
                                  {item.description && (
                                    <span className="block text-foreground-500 text-xs mt-0.5 leading-relaxed">
                                      {item.description}
                                    </span>
                                  )}
                                </div>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-20">
                      <ul className="space-y-5 flex-1">
                        {activeMenu.items.slice(0, Math.ceil(activeMenu.items.length / 2)).map((item) => (
                          <li key={item.label}>
                            <a
                              href={item.href}
                              className="group flex items-start gap-3"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center shrink-0 group-hover:bg-accent-100 transition-colors duration-200">
                                <i className={`${getSolutionIcon(item.label)} w-4 h-4 flex items-center justify-center text-accent-500`} />
                              </span>
                              <div>
                                <span className="block font-heading font-semibold text-foreground-900 group-hover:text-accent-500 transition-colors duration-200 text-sm leading-tight">
                                  {item.label}
                                </span>
                                {item.description && (
                                  <span className="block text-foreground-500 text-xs mt-0.5 leading-relaxed">
                                    {item.description}
                                  </span>
                                )}
                              </div>
                            </a>
                          </li>
                        ))}
                      </ul>
                      <ul className="space-y-5 flex-1">
                        {activeMenu.items.slice(Math.ceil(activeMenu.items.length / 2)).map((item) => (
                          <li key={item.label}>
                            <a
                              href={item.href}
                              className="group flex items-start gap-3"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center shrink-0 group-hover:bg-accent-100 transition-colors duration-200">
                                <i className={`${getSolutionIcon(item.label)} w-4 h-4 flex items-center justify-center text-accent-500`} />
                              </span>
                              <div>
                                <span className="block font-heading font-semibold text-foreground-900 group-hover:text-accent-500 transition-colors duration-200 text-sm leading-tight">
                                  {item.label}
                                </span>
                                {item.description && (
                                  <span className="block text-foreground-500 text-xs mt-0.5 leading-relaxed">
                                    {item.description}
                                  </span>
                                )}
                              </div>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Fixed phone button — bottom right, visible only when navbar is hidden */}
      <a
        href="tel:+492822791010"
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-accent-500 flex items-center justify-center shadow-lg transition-opacity duration-300 ${
          navHidden && !isAnyDropdownOpen && !mobileOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        aria-label="Anrufen"
      >
        <i className="ri-phone-line w-6 h-6 flex items-center justify-center text-white text-lg" />
      </a>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <nav id="mobile-menu" aria-label="Mobile Navigation" className="fixed inset-0 z-40 bg-white pt-28 px-6 overflow-y-auto">
          <div className="space-y-5 pb-10">
            {dropdownMenus.map((menu) => (
              <div key={menu.id} className="border-b border-foreground-200 pb-4">
                <button
                  onClick={() => toggleDropdown(menu.id)}
                  aria-expanded={activeDropdown === menu.id}
                  aria-controls={`mobile-dropdown-${menu.id}`}
                  className="flex items-center justify-between w-full text-left font-heading font-semibold text-foreground-900 text-xl"
                >
                  {menu.label}
                  <i
                    aria-hidden="true"
                    className={`ri-arrow-down-s-line w-6 h-6 flex items-center justify-center transition-transform ${
                      activeDropdown === menu.id ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {activeDropdown === menu.id && (
                  <div id={`mobile-dropdown-${menu.id}`} className="mt-4 grid grid-cols-1 sm:grid-cols-[200px_1fr] md:grid-cols-[260px_1fr] gap-4 animate-dropdown-enter">

                    {/* Bild links */}
                    {menu.image && (
                      <div className="relative overflow-hidden rounded-xl h-40 sm:h-full sm:min-h-[220px]">
                        <img
                          src={menu.image}
                          alt={menu.imageAlt}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                        <span className="absolute bottom-3 left-3 right-3 font-heading font-semibold text-white text-sm uppercase tracking-wider">
                          {menu.label}
                        </span>
                      </div>
                    )}

                    {/* Untermenü rechts mit Icons */}
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {[...menu.items, ...(menu.secondaryItems ?? [])].map((item) => (
                        <li key={item.label}>
                          <a
                            href={item.href}
                            className="group flex items-start gap-3 p-3 rounded-xl bg-background-100 hover:bg-accent-50 transition-colors"
                            onClick={() => setMobileOpen(false)}
                          >
                            <span className="w-9 h-9 rounded-lg bg-white flex items-center justify-center shrink-0 group-hover:bg-accent-100 transition-colors">
                              <i className={`${getSolutionIcon(item.label)} w-4 h-4 flex items-center justify-center text-accent-500`} />
                            </span>
                            <div className="min-w-0">
                              <span className="block font-heading font-semibold text-foreground-900 group-hover:text-accent-600 transition-colors text-sm leading-tight">
                                {item.label}
                              </span>
                              {item.description && (
                                <span className="block text-foreground-500 text-xs mt-0.5 leading-snug">
                                  {item.description}
                                </span>
                              )}
                            </div>
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}

            <div className="border-b border-foreground-200 pb-4">
              <a
                href="/referenzen"
                onClick={() => setMobileOpen(false)}
                className="block font-heading font-semibold text-foreground-900 text-xl hover:text-accent-600 transition-colors"
              >
                Referenzen
              </a>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <a
                href="tel:+492822791010"
                className="flex items-center gap-2 text-foreground-700 hover:text-accent-500 transition-colors text-sm"
              >
                <span className="relative inline-flex w-4 h-4 items-center justify-center">
                  {phoneIconPulse && (
                    <span
                      className="absolute inset-0 rounded-full opacity-50 motion-safe:animate-ping motion-reduce:hidden"
                      style={{ backgroundColor: phoneIconColor }}
                      aria-hidden="true"
                    />
                  )}
                  <i
                    className="ri-phone-line w-4 h-4 flex items-center justify-center relative"
                    style={phoneIconColor ? { color: phoneIconColor } : undefined}
                  />
                </span>
                02822 791010
              </a>
              <LiveStatusBadge variant="dark" />
            </div>

            <div className="flex items-center gap-2 text-foreground-400 text-sm">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
              Kundenportal <span className="text-xs">(in Kürze)</span>
            </div>

            <a
              href="/kontakt"
              onClick={() => setMobileOpen(false)}
              className="inline-flex items-center px-6 py-3 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-colors"
            >
              Angebot anfragen
            </a>
          </div>
        </nav>

      )}
    </>
  );
}